import React, { useState, useEffect } from 'react';
import { owedAmount } from '../utils/balanceSign';
import { accountsAPI, creditorsAPI } from '../services/api';
import apiClient from '../shared/services/apiClient';
import { formatApiError } from '../utils/errorMessage';
import { isPaymentUnconfirmed, PAYMENT_UNCONFIRMED, BILL_REPLAYED } from '../utils/idempotencyKey';
import { todayLocal, parseCalendarDay } from '../utils/localDate';
import { parseMoneyInput, negateMoneyString, MONEY_FIELD_ERROR } from '../utils/money';
import { Link } from 'react-router-dom';
import SearchableSelect from './SearchableSelect';
import { pickerValue, linkableAccounts, findAccountById } from '../utils/linkableAccounts';

const VehicleLiabilities = ({ vehicleId, vehicleName, selectedBusiness }) => {
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLiability, setEditingLiability] = useState(null);
  const [creditors, setCreditors] = useState([]);
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [payingId, setPayingId] = useState(null);
  const [linkMode, setLinkMode] = useState('create'); // 'create' or 'link'

  const [formData, setFormData] = useState({
    account_id: '', // For linking existing account
    creditor_id: '',
    account_name: '',
    account_type: 'loan',
    account_number_last4: '',
    current_balance: '',
    interest_rate: '',
    amount_due: '',
    due_frequency: 'monthly',
    next_due_date: '',
    reminder_days_before: 14,
    is_auto_pay: false,
    notes: '',
    is_active: true,
  });

  useEffect(() => {
    if (vehicleId) {
      loadLiabilities();
      loadCreditors();
      loadExistingAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, selectedBusiness]);

  const loadCreditors = async () => {
    try {
      // Only fetch creditors for the selected business + global creditors
      const params = { is_active: true };
      if (selectedBusiness) {
        params.business_id = selectedBusiness;
      }
      const response = await creditorsAPI.getAll(params);
      setCreditors(response.data || []);
    } catch (err) {
      console.error('Error loading creditors:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
    }
  };

  const loadExistingAccounts = async () => {
    try {
      // Load all liability accounts that aren't linked to this vehicle
      const response = await accountsAPI.getAll({
        is_liability: 'true',
        is_active: 'true', ...(selectedBusiness ? { business_id: selectedBusiness } : {}),
      });
      // Filter out accounts already linked to this vehicle
      const unlinkedAccounts = linkableAccounts(response.data, 'vehicle_id', vehicleId);
      setExistingAccounts(unlinkedAccounts);
    } catch (err) {
      console.error('Error loading existing accounts:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
    }
  };

  const loadLiabilities = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await accountsAPI.getAll({ 
        vehicle_id: vehicleId, 
        is_liability: 'true' 
      });
      setLiabilities(response.data || []);
    } catch (err) {
      console.error('Error loading liabilities:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      setError(formatApiError(err, 'Failed to load liabilities'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');

      const amountDue = parseMoneyInput(formData.amount_due);
      const balance = parseMoneyInput(formData.current_balance, { allowNegative: true });
      if (amountDue.error || (amountDue.value === null && editingLiability?.recurring_bill_id)) {
        setError(`Amount due: ${MONEY_FIELD_ERROR}`);
        return;
      }
      if (balance.error) {
        setError(`Current balance: ${MONEY_FIELD_ERROR}`);
        return;
      }
      let accountId;

      if (linkMode === 'link' && !editingLiability) {
        // Link existing account to this vehicle
        if (!formData.account_id) {
          setError('Please select an account to link');
          return;
        }
        
        const selectedAccount = findAccountById(existingAccounts, formData.account_id);
        if (!selectedAccount) {
          setError('Selected account not found');
          return;
        }

        // Update account to link it to this vehicle
        await accountsAPI.update(selectedAccount.id, {
          vehicle_id: vehicleId,
        });
        accountId = selectedAccount.id;

        // If recurring bill data is provided, create/update it
        if (amountDue.value !== null) {
          if (selectedAccount.recurring_bill_id) {
            await apiClient.put(`/recurring-bills/${selectedAccount.recurring_bill_id}`, {
              account_id: accountId,
              bill_name: selectedAccount.account_name,
              payee: selectedAccount.creditor_name || '',
              amount: amountDue.value,
              frequency: formData.due_frequency,
              next_due_date: formData.next_due_date,
              creditor_id: selectedAccount.creditor_id,
              is_auto_pay: formData.is_auto_pay,
              reminder_days_before: formData.reminder_days_before || 14,
              is_active: formData.is_active,
              notes: formData.notes || null,
            });
          } else {
            await apiClient.post('/recurring-bills', {
              account_id: accountId,
              bill_name: selectedAccount.account_name,
              payee: selectedAccount.creditor_name || '',
              amount: amountDue.value,
              frequency: formData.due_frequency,
              next_due_date: formData.next_due_date,
              creditor_id: selectedAccount.creditor_id,
              is_auto_pay: formData.is_auto_pay,
              reminder_days_before: formData.reminder_days_before || 14,
              is_active: formData.is_active,
              notes: formData.notes || null,
            });
          }
        }
      } else {
        // Create new account or update existing
        if (!formData.creditor_id) {
          setError('Creditor is required');
          return;
        }

        const accountData = {
          creditor_id: parseInt(formData.creditor_id),
          account_name: formData.account_name,
          account_type: formData.account_type,
          account_number_last4: formData.account_number_last4 || null,
          current_balance: balance.value === null ? 0 : negateMoneyString(balance.value.replace(/^-/, '')),
          interest_rate: formData.interest_rate || null,
          vehicle_id: vehicleId,
          is_liability: true,
          is_active: formData.is_active,
          notes: formData.notes || null,
        };

        if (editingLiability) {
          await accountsAPI.update(editingLiability.id, accountData);
          accountId = editingLiability.id;
          
          if (editingLiability.recurring_bill_id) {
            await apiClient.put(`/recurring-bills/${editingLiability.recurring_bill_id}`, {
              account_id: accountId,
              bill_name: formData.account_name,
              payee: creditors.find(c => c.id === parseInt(formData.creditor_id))?.creditor_name || '',
              amount: amountDue.value,
              frequency: formData.due_frequency,
              next_due_date: formData.next_due_date,
              creditor_id: parseInt(formData.creditor_id),
              is_auto_pay: formData.is_auto_pay,
              reminder_days_before: formData.reminder_days_before || 14,
              is_active: formData.is_active,
              notes: formData.notes || null,
            });
          } else if (amountDue.value !== null) {
            await apiClient.post('/recurring-bills', {
              account_id: accountId,
              bill_name: formData.account_name,
              payee: creditors.find(c => c.id === parseInt(formData.creditor_id))?.creditor_name || '',
              amount: amountDue.value,
              frequency: formData.due_frequency,
              next_due_date: formData.next_due_date,
              creditor_id: parseInt(formData.creditor_id),
              is_auto_pay: formData.is_auto_pay,
              reminder_days_before: formData.reminder_days_before || 14,
              is_active: formData.is_active,
              notes: formData.notes || null,
            });
          }
        } else {
          const accountResponse = await accountsAPI.create(accountData);
          accountId = accountResponse.data.id;
          
          if (amountDue.value !== null) {
            await apiClient.post('/recurring-bills', {
              account_id: accountId,
              bill_name: formData.account_name,
              payee: creditors.find(c => c.id === parseInt(formData.creditor_id))?.creditor_name || '',
              amount: amountDue.value,
              frequency: formData.due_frequency,
              next_due_date: formData.next_due_date,
              creditor_id: parseInt(formData.creditor_id),
              is_auto_pay: formData.is_auto_pay,
              reminder_days_before: formData.reminder_days_before || 14,
              is_active: formData.is_active,
              notes: formData.notes || null,
            });
          }
        }
      }

      setShowModal(false);
      resetForm();
      loadLiabilities();
      loadExistingAccounts(); // Reload to update available accounts
    } catch (err) {
      console.error('Error saving liability:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      setError(formatApiError(err, 'Failed to save liability'));
    }
  };

  const handleEdit = (liability) => {
    setEditingLiability(liability);
    setFormData({
      creditor_id: liability.creditor_id || '',
      account_name: liability.account_name || '',
      account_type: liability.account_type || 'loan',
      account_number_last4: liability.account_number_last4 || '',
      current_balance: liability.current_balance ? Math.abs(liability.current_balance) : '',
      interest_rate: liability.interest_rate || '',
      amount_due: liability.amount_due || '',
      due_frequency: liability.due_frequency || 'monthly',
      next_due_date: liability.next_due_date?.split('T')[0] || '',
      reminder_days_before: liability.reminder_days_before || 14,
      is_auto_pay: liability.is_auto_pay || false,
      notes: liability.notes || '',
      is_active: liability.is_active !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to unlink this account from this vehicle? The account will remain but will no longer be associated with this vehicle.')) return;
    
    try {
      // Unlink account from vehicle (set vehicle_id to null)
      await accountsAPI.update(id, {
        vehicle_id: null,
      });
      loadLiabilities();
      loadExistingAccounts(); // Reload available accounts
    } catch (err) {
      console.error('Error unlinking liability:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      alert(formatApiError(err, 'Failed to unlink account'));
    }
  };

  const handleMarkPaid = async (liability) => {
    if (payingId) return;
    setNotice('');
    setPayingId(liability.id);
    try {
      const response = await apiClient.post(`/recurring-bills/${liability.recurring_bill_id}/pay`, {
        payment_date: todayLocal(),
        expected_due_date: liability.next_due_date_key
      });
      if (response.data?.replayed) setNotice(BILL_REPLAYED);
      loadLiabilities();
    } catch (err) {
      console.error('Error marking as paid:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      alert(isPaymentUnconfirmed(err) ? PAYMENT_UNCONFIRMED : formatApiError(err, 'Failed to mark as paid'));
    } finally {
      setPayingId(null);
    }
  };

  const resetForm = () => {
    setEditingLiability(null);
    setLinkMode('create');
    setFormData({
      account_id: '',
      creditor_id: '',
      account_name: '',
      account_type: 'loan',
      account_number_last4: '',
      current_balance: '',
      interest_rate: '',
      amount_due: '',
      due_frequency: 'monthly',
      next_due_date: '',
      reminder_days_before: 14,
      is_auto_pay: false,
      notes: '',
      is_active: true,
    });
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return parseCalendarDay(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getLiabilityTypeLabel = (type) => {
    const labels = {
      loan: 'Loan',
      credit_card: 'Credit Card',
      line_of_credit: 'Line of Credit',
      vehicle_loan: 'Vehicle Loan',
      vehicle_lease: 'Vehicle Lease',
      insurance: 'Insurance',
      registration: 'Registration',
      maintenance: 'Maintenance',
      other: 'Other',
    };
    return labels[type] || type?.replace(/_/g, ' ') || 'N/A';
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semi_annual: 'Semi-Annual',
      annual: 'Annual',
      one_time: 'One Time',
      weekly: 'Weekly',
      biweekly: 'Bi-weekly',
    };
    return labels[freq] || freq || 'N/A';
  };

  if (loading) {
    return <div className="text-center py-4 text-gray-500">Loading liabilities...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Vehicle Liabilities</h3>
          {vehicleName && <p className="text-sm text-gray-500">{vehicleName}</p>}
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Add Liability
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm" role="alert">
          {error}
        </div>
      )}

      {notice && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-800 text-sm" role="status">
          {notice}
        </div>
      )}

      {liabilities.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No liabilities found for this vehicle.</p>
          <p className="text-sm mt-2">Add loans, leases, insurance, and other recurring expenses.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creditor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount Due</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Due</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {liabilities.map((liability) => (
                <tr key={liability.id} className={!liability.is_active ? 'opacity-50' : ''}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {getLiabilityTypeLabel(liability.account_type)}
                    </span>
                    {liability.account_number_last4 && (
                      <p className="text-xs text-gray-500">#{liability.account_number_last4}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">{liability.account_name}</div>
                    <Link 
                      to={`/transactions?account_id=${liability.id}`}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      View Transactions →
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">{liability.creditor_name || 'N/A'}</div>
                    {liability.creditor_phone && (
                      <div className="text-xs text-gray-500">{liability.creditor_phone}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-red-600">
                    {formatCurrency(owedAmount(liability))}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {liability.amount_due ? formatCurrency(liability.amount_due) : 'N/A'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                    {liability.due_frequency ? getFrequencyLabel(liability.due_frequency) : 'N/A'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(liability.next_due_date)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      liability.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {liability.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      {liability.recurring_bill_id && (
                        <button
                          onClick={() => handleMarkPaid(liability)}
                          disabled={payingId !== null}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={payingId === liability.id ? 'Saving...' : 'Mark as Paid'}
                          aria-label={payingId === liability.id ? 'Saving' : 'Mark as paid'}
                        >
                          {payingId === liability.id ? 'Saving...' : '💰'}
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(liability)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(liability.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Unlink
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">
                {editingLiability ? 'Edit Liability' : 'Add New Liability'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Mode Selection */}
                {!editingLiability && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium mb-2">Link Existing Account or Create New?</label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="link"
                          checked={linkMode === 'link'}
                          onChange={(e) => setLinkMode(e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm">Link Existing Account</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="create"
                          checked={linkMode === 'create'}
                          onChange={(e) => setLinkMode(e.target.value)}
                          className="mr-2"
                        />
                        <span className="text-sm">Create New Account</span>
                      </label>
                    </div>
                  </div>
                )}

                {linkMode === 'link' && !editingLiability ? (
                  // Link existing account mode
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Select Account to Link *</label>
                      <SearchableSelect
                        value={formData.account_id}
                        onChange={(arg) => {
                          const value = pickerValue(arg);
                          const selected = findAccountById(existingAccounts, value);
                          setFormData({ 
                            ...formData, 
                            account_id: value,
                            creditor_id: selected?.creditor_id || '',
                          });
                        }}
                        options={existingAccounts}
                        placeholder="Search and select an account..."
                        getOptionLabel={(option) => `${option.account_name} - ${option.creditor_name || 'No Creditor'} (${formatCurrency(owedAmount(option))})`}
                        getOptionValue={(option) => option.id}
                        className="w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Select an existing liability account to link to this vehicle
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Amount Due (Per Period)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.amount_due}
                        onChange={(e) => setFormData({ ...formData, amount_due: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Due Frequency</label>
                      <select
                        value={formData.due_frequency}
                        onChange={(e) => setFormData({ ...formData, due_frequency: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="semi_annual">Semi-Annual</option>
                        <option value="annual">Annual</option>
                        <option value="one_time">One Time</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Next Due Date</label>
                      <input
                        type="date"
                        value={formData.next_due_date}
                        onChange={(e) => setFormData({ ...formData, next_due_date: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Reminder Days Before</label>
                      <input
                        type="number"
                        value={formData.reminder_days_before}
                        onChange={(e) => setFormData({ ...formData, reminder_days_before: parseInt(e.target.value) || 14 })}
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Notes</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        rows="3"
                        placeholder="Additional notes..."
                      />
                    </div>

                    <div className="md:col-span-2 flex items-center gap-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_auto_pay}
                          onChange={(e) => setFormData({ ...formData, is_auto_pay: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">Auto-Pay Enabled</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">Active</span>
                      </label>
                    </div>
                  </div>
                ) : (
                  // Create new account mode (existing form)
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">Creditor *</label>
                      <select
                        value={formData.creditor_id}
                        onChange={(e) => setFormData({ ...formData, creditor_id: e.target.value })}
                        className="w-full border rounded px-3 py-2"
                        required={linkMode === 'create'}
                      >
                        <option value="">Select Creditor</option>
                        {creditors.map((creditor) => (
                          <option key={creditor.id} value={creditor.id}>
                            {creditor.creditor_name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        <Link to="/creditors" className="text-blue-600 hover:text-blue-800">
                          Add new creditor →
                        </Link>
                      </p>
                    </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Account Name *</label>
                    <input
                      type="text"
                      value={formData.account_name}
                      onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="e.g., Auto Loan"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Account Type *</label>
                    <select
                      value={formData.account_type}
                      onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required
                    >
                      <option value="loan">Loan</option>
                      <option value="vehicle_loan">Vehicle Loan</option>
                      <option value="vehicle_lease">Vehicle Lease</option>
                      <option value="insurance">Insurance</option>
                      <option value="registration">Registration</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Account Number (Last 4)</label>
                    <input
                      type="text"
                      value={formData.account_number_last4}
                      onChange={(e) => setFormData({ ...formData, account_number_last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="1234"
                      maxLength="4"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Current Balance</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.current_balance}
                      onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Interest Rate (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.interest_rate}
                      onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Amount Due (Per Period)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount_due}
                      onChange={(e) => setFormData({ ...formData, amount_due: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Due Frequency</label>
                    <select
                      value={formData.due_frequency}
                      onChange={(e) => setFormData({ ...formData, due_frequency: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semi_annual">Semi-Annual</option>
                      <option value="annual">Annual</option>
                      <option value="one_time">One Time</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Next Due Date</label>
                    <input
                      type="date"
                      value={formData.next_due_date}
                      onChange={(e) => setFormData({ ...formData, next_due_date: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Reminder Days Before</label>
                    <input
                      type="number"
                      value={formData.reminder_days_before}
                      onChange={(e) => setFormData({ ...formData, reminder_days_before: parseInt(e.target.value) || 14 })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      rows="3"
                      placeholder="Additional notes..."
                    />
                  </div>

                  <div className="md:col-span-2 flex items-center gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_auto_pay}
                        onChange={(e) => setFormData({ ...formData, is_auto_pay: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm">Auto-Pay Enabled</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm">Active</span>
                    </label>
                  </div>
                </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm" role="alert">
                    {error}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingLiability ? 'Update' : linkMode === 'link' ? 'Link Account' : 'Create Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleLiabilities;
